import { useMemo, useCallback, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../../stateManagement/hooks/redux";
import { applyUserMessageWindowOpened, setSidebarMenuPath } from "../../../stateManagement/reducers/SidebarBlockSlice";
import { typesRequiringPathId } from "./menuListFunctions";
import { Tooltip } from "primereact/tooltip";
import { getItemWebStorage, setItemWebStorage } from "../../../features/webStorageSaver";
import { getIndorImageSrc } from "../../../features";
import rightArrow from "../../../assets/images/fontAwesomeIcons/chevron-right-solid.svg";
import { logoutApp } from "../../../features/auth/logout";
import { useCheckIndorIDAuthQuery } from "../../../api/rtk/indorsoftRu/slices/indorIDAuthApiSlice";
import { useGetUnreadTicketStatsQuery } from "../../../api/rtk/indorsoftRu/slices/supportApiSlice";

type MenuListItemProps = {
    id: string;
    type: string;
    index: number;
    title: string;
    imageIndex?: string;
    imageType?: "localIcon" | "remote";
    url?: string;
    hasChildren?: boolean;
    isPathRequired?: boolean;
};

export default function MenuListItem({
    id,
    type,
    index,
    title,
    imageIndex = "",
    imageType = "remote",
    url,
    hasChildren = false,
    isPathRequired = false,
}: MenuListItemProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const currentMenuPath = useAppSelector((state) => state.sidebar.sidebarMenuPath);
    const pathId = useAppSelector((state) => state.pathsInfo.selectedPath?.pathId);

    const currentConfig = getItemWebStorage("currentConfig");
    const isRoadsListPageAvailable = Boolean(currentConfig?.menuShow?.road);
    const isSupportEnabledInConfig = currentConfig?.support?.enabled ?? false;

    // Проверка авторизации IndorID и непрочитанных сообщений для пункта "Справка"
    const { data: authData } = useCheckIndorIDAuthQuery(undefined, {
        skip: type !== 'help' || !isSupportEnabledInConfig, // Проверяем только для пункта "Справка"
    });

    const { data: unreadStats } = useGetUnreadTicketStatsQuery(undefined, { 
        skip: type !== 'help' || !isSupportEnabledInConfig || !authData?.authorized,
        pollingInterval: 45000,
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
    });
    const hasUnreadMessages = isSupportEnabledInConfig && (unreadStats?.hasUnread ?? false);

    const isActive = useMemo(() => (url ? location.pathname === url : false), [location.pathname, url]);

    const requiresPath = useMemo(
        () => Boolean(isPathRequired) || typesRequiringPathId.includes(type),
        [isPathRequired, type]
    );

    const isEnabled = useMemo(() => {
        return !requiresPath || !isRoadsListPageAvailable || (requiresPath && !!pathId);
    }, [requiresPath, isRoadsListPageAvailable, pathId]);

    const classNames = useMemo(() => {
        return [
            "menu-list-item",
            `menu-item-${id}`,
            id,
            isActive && "active",
            !isEnabled && "disabled",
        ]
            .filter(Boolean)
            .join(" ");
    }, [id, isActive, isEnabled]);

    const changeCurrentMenuPath = useCallback(() => {
        if (hasChildren) {
            const newMenuPath = [...currentMenuPath, index];
            dispatch(setSidebarMenuPath(newMenuPath));
            const sidebarLocalSettings = getItemWebStorage("sidebar");
            setItemWebStorage("sidebar", { ...sidebarLocalSettings, menuPath: newMenuPath });
        }
        if (url) navigate(url);
    }, [hasChildren, currentMenuPath, index, dispatch, url, navigate]);

    const onClick = useCallback(() => {
        if (!isEnabled) return;
        if (type === "feedback") {
            dispatch(applyUserMessageWindowOpened(true));
        } else if (type === "exit") {
            logoutApp()
        } else {
            changeCurrentMenuPath();
        }
    }, [isEnabled, type, dispatch, changeCurrentMenuPath]);

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (!isEnabled) return;
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
            }
        },
        [isEnabled, onClick]
    );

    function getMenuItemImageSrcLocal(idx: string): string | undefined {
        try {
            const mod = require(`../../../assets/images/icons/menuItems/${idx}.png`);
            return mod?.default ?? mod;
        } catch {
            return undefined;
        }
    }

    const tooltipTarget = `.menu-item-${id}`;

    return (
        <>
            {!isEnabled && (
                <Tooltip target={tooltipTarget} mouseTrack mouseTrackLeft={15} />
            )}
            <div
                className={classNames}
                role="button"
                tabIndex={isEnabled ? 0 : -1}
                aria-disabled={!isEnabled}
                onClick={isEnabled ? onClick : undefined}
                onKeyDown={onKeyDown}
                data-pr-tooltip={!isEnabled ? "Не выбрана дорога" : ""}
            >
                {imageType === "localIcon" ? (
                    <img
                        className="menu-list-item-img"
                        src={imageIndex ? getMenuItemImageSrcLocal(imageIndex) : undefined}
                        alt=""
                        loading="lazy"
                    />
                ) : (
                    <img
                        className="menu-list-item-img"
                        src={getIndorImageSrc(imageIndex)}
                        alt=""
                        loading="lazy"
                    />
                )}

                <span className="menu-list-item-title">{title}</span>

                {type === 'help' && hasUnreadMessages && (
                    <span className="menu-list-item-unread-indicator">!</span>
                )}

                {hasChildren && (
                    <img
                        className="menu-list-item-right-arrow-img"
                        src={rightArrow}
                        alt=""
                        loading="lazy"
                    />
                )}
            </div>
        </>
    );
}
